import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import bankService from "../../../services/bank.service";
import { formatVehicleNumberDisplay } from "../../../utils/vehicleNumberUtils";
import "../../../styles/bankDetails.css";
import "../../../styles/confirmation.css";
import "../../../styles/bankTraceCases.css";

function formatTracedAt(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function BankTraceCaseDetail() {
  const { id } = useParams();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    bankService
      .getTraceCase(id)
      .then((res) => {
        if (cancelled) return;
        setRow(res?.data?.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || "Trace case not found.");
        setRow(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const fields = row
    ? [
        ["Vehicle Number", formatVehicleNumberDisplay(row.vehicleNumber)],
        ["Loan Number", row.loanNumber || "—"],
        ["Customer Name", row.customerName || "—"],
        ["Trace By", row.traceBy || "—"],
        ["Trace Location", row.traceLocation || "—"],
        ["Trace Date & Time", formatTracedAt(row.tracedAt)],
        ["Status", row.status || "Traced"],
      ]
    : [];

  const lat = Number(row?.latitude);
  const lng = Number(row?.longitude);
  const hasGps = Number.isFinite(lat) && Number.isFinite(lng);
  const mapsUrl = String(row?.mapsUrl || "").trim();
  const mapEmbed = hasGps
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.02},${lat - 0.02},${lng + 0.02},${lat + 0.02}&layer=mapnik&marker=${lat},${lng}`
    : "";

  return (
    <div className="bd-page cf-page bank-trace-detail">
      <p>
        <Link to="/bank/trace-cases" className="secondary-page-btn bank-trace-detail__back">
          <FiArrowLeft aria-hidden /> Back to Trace Cases
        </Link>
      </p>
      <h2>Trace Case</h2>

      {loading ? <p className="cf-excel-loading">Loading case…</p> : null}
      {error ? <p className="bd-feedback bd-error">{error}</p> : null}

      {!loading && row ? (
        <dl className="bank-trace-detail__dl">
          {fields.map(([label, value]) => (
            <div key={label} className="bank-trace-detail__row">
              <dt>{label}</dt>
              <dd>
                {label === "Trace Location" ? (
                  <span className="bank-trace-detail__location">
                    <span>{value}</span>
                    {mapsUrl ? (
                      <a
                        className="bank-trace-detail__maps"
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View on Map
                      </a>
                    ) : null}
                  </span>
                ) : (
                  value
                )}
              </dd>
            </div>
          ))}
          {mapEmbed ? (
            <div className="bank-trace-detail__row">
              <dt>Map</dt>
              <dd>
                <div className="bank-trace-detail__map">
                  <iframe
                    title="Trace location map"
                    src={mapEmbed}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}
